import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, Search, Users, Building, Phone, Mail, MapPin, Tag, 
  Edit, Trash2, FileText, Image, CheckSquare, MessageSquare, History, ChevronLeft,
  Smartphone, Printer, Briefcase, Loader2, Download, X, Share2, FileDown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const ContactsPage = () => {
  const { user } = useAuth();
  const t = useTranslation(user?.language || 'ja');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDashboard, setContactDashboard] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  // Form state
  const [form, setForm] = useState({
    name: '',
    name_kana: '',
    name_romaji: '',
    title: '',
    company: '',
    catchphrase: '',
    phone: '',
    mobile: '',
    fax: '',
    email: '',
    address: '',
    tags: '',
    status: 'active',
    notes: '',
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchContacts();
  }, [debouncedSearch, statusFilter]);

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const { data } = await axios.get(`${API_URL}/api/contacts?${params}`, {
        withCredentials: true,
      });
      setContacts(data.contacts);
    } catch (e) {
      console.error('Failed to fetch contacts:', e);
      toast.error(t.common.error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContactDashboard = async (contactId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/contacts/${contactId}/dashboard`, {
        withCredentials: true,
      });
      setContactDashboard(data);
    } catch (e) {
      console.error('Failed to fetch contact dashboard:', e);
    }
  };

  const handleSelectContact = (contact) => {
    setSelectedContact(contact);
    fetchContactDashboard(contact.id);
  };

  const handleCreate = async () => {
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      await axios.post(`${API_URL}/api/contacts`, payload, {
        withCredentials: true,
      });
      toast.success(t.common.success);
      setIsCreateOpen(false);
      setForm({ name: '', name_kana: '', name_romaji: '', title: '', company: '', catchphrase: '', phone: '', mobile: '', fax: '', email: '', address: '', tags: '', status: 'active', notes: '' });
      fetchContacts();
    } catch (e) {
      toast.error(t.common.error);
    }
  };

  const handleUpdate = async () => {
    if (!selectedContact) return;
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      await axios.put(`${API_URL}/api/contacts/${selectedContact.id}`, payload, {
        withCredentials: true,
      });
      toast.success(t.common.success);
      setIsEditOpen(false);
      fetchContacts();
      fetchContactDashboard(selectedContact.id);
    } catch (e) {
      toast.error(t.common.error);
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm(t.common.confirm)) return;
    try {
      await axios.delete(`${API_URL}/api/contacts/${contactId}`, {
        withCredentials: true,
      });
      toast.success(t.common.success);
      setSelectedContact(null);
      setContactDashboard(null);
      fetchContacts();
    } catch (e) {
      toast.error(t.common.error);
    }
  };

  const openEditDialog = (contact) => {
    setForm({
      name: contact.name || '',
      name_kana: contact.name_kana || '',
      name_romaji: contact.name_romaji || '',
      title: contact.title || '',
      company: contact.company || '',
      catchphrase: contact.catchphrase || '',
      phone: contact.phone || '',
      mobile: contact.mobile || '',
      fax: contact.fax || '',
      email: contact.email || '',
      address: contact.address || '',
      tags: (contact.tags || []).join(', '),
      status: contact.status || 'active',
      notes: contact.notes || '',
    });
    setIsEditOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(user?.language === 'en' ? 'en-US' : 'ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const ja = user?.language === 'ja';

  const ContactForm = ({ onSubmit, submitText }) => (
    <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
      {/* 名前セクション */}
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{ja ? '氏名情報' : 'Name'}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">{ja ? '氏名' : 'Name'} *</Label>
            <Input value={form.name} onChange={handleNameChange} className="input-cyber h-9" data-testid="contact-name-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1">
              {ja ? '読み仮名' : 'Furigana'}
              {furiganaLoading && <Loader2 className="w-3 h-3 animate-spin text-primary" />}
            </Label>
            <Input value={form.name_kana} onChange={(e) => setForm({ ...form, name_kana: e.target.value })} className="input-cyber h-9" placeholder={ja ? 'ヤマダ タロウ' : ''} data-testid="contact-kana-input" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">{ja ? '名前のスペル' : 'Romaji'}</Label>
            <Input value={form.name_romaji} onChange={(e) => setForm({ ...form, name_romaji: e.target.value })} className="input-cyber h-9" placeholder="Taro Yamada" data-testid="contact-romaji-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{ja ? '肩書' : 'Title'}</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-cyber h-9" placeholder={ja ? '代表取締役' : 'CEO'} data-testid="contact-title-input" />
          </div>
        </div>
      </div>

      {/* 会社セクション */}
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{ja ? '会社情報' : 'Company'}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">{ja ? '社名' : 'Company'}</Label>
            <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input-cyber h-9" data-testid="contact-company-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{ja ? 'キャッチコピー' : 'Catchphrase'}</Label>
            <Input value={form.catchphrase} onChange={(e) => setForm({ ...form, catchphrase: e.target.value })} className="input-cyber h-9" data-testid="contact-catchphrase-input" />
          </div>
        </div>
      </div>

      {/* 連絡先セクション */}
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{ja ? '連絡先' : 'Contact Info'}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">{ja ? '電話番号' : 'Phone'}</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-cyber h-9" data-testid="contact-phone-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{ja ? '携帯電話' : 'Mobile'}</Label>
            <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="input-cyber h-9" data-testid="contact-mobile-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{ja ? 'FAX' : 'Fax'}</Label>
            <Input value={form.fax} onChange={(e) => setForm({ ...form, fax: e.target.value })} className="input-cyber h-9" data-testid="contact-fax-input" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">{ja ? 'メール' : 'Email'}</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-cyber h-9" data-testid="contact-email-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{ja ? '住所' : 'Address'}</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input-cyber h-9" data-testid="contact-address-input" />
          </div>
        </div>
      </div>

      {/* その他 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">{t.contacts.tags}</Label>
          <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder={ja ? 'タグ1, タグ2' : 'tag1, tag2'} className="input-cyber h-9" data-testid="contact-tags-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t.contacts.status}</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger className="input-cyber h-9" data-testid="contact-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">{t.contacts.active}</SelectItem>
              <SelectItem value="inactive">{t.contacts.inactive}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t.contacts.notes}</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-cyber min-h-[60px]" data-testid="contact-notes-input" />
      </div>
      <Button onClick={onSubmit} className="w-full btn-primary" data-testid="contact-submit-btn">{submitText}</Button>
    </div>
  );

  // Mobile: show detail view when a contact is selected
  const [mobileShowDetail, setMobileShowDetail] = useState(false);
  const [furiganaLoading, setFuriganaLoading] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const furiganaTimer = useRef(null);

  // Auto-generate furigana when name changes
  const fetchFurigana = useCallback(async (name) => {
    if (!name || name.length < 2) return;
    // Check if name contains Japanese characters
    if (!/[\u3000-\u9fff\uf900-\ufaff]/.test(name)) return;
    
    setFuriganaLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/contacts/furigana`, { name });
      if (res.data.furigana) {
        setForm(prev => ({ ...prev, name_kana: res.data.furigana }));
      }
    } catch (e) {
      // Silently fail
    } finally {
      setFuriganaLoading(false);
    }
  }, []);

  const handleNameChange = (e) => {
    const name = e.target.value;
    setForm(prev => ({ ...prev, name }));
    // Debounce furigana fetch
    if (furiganaTimer.current) clearTimeout(furiganaTimer.current);
    furiganaTimer.current = setTimeout(() => {
      if (!form.name_kana || form.name_kana === '') {
        fetchFurigana(name);
      }
    }, 800);
  };

  const handleSelectContactMobile = (contact) => {
    handleSelectContact(contact);
    setMobileShowDetail(true);
  };

  const handleBackToList = () => {
    setMobileShowDetail(false);
  };

  // === Export Functions ===
  const [googleExporting, setGoogleExporting] = useState(false);

  const handleDownloadVcard = async (contactId, contactName) => {
    try {
      const response = await axios.get(`${API_URL}/api/contacts/${contactId}/vcard`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/vcard' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(contactName || 'contact').replace(/\s/g, '_')}.vcf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(ja ? 'vCardをダウンロードしました' : 'vCard downloaded');
    } catch (e) {
      toast.error(ja ? 'ダウンロードに失敗しました' : 'Download failed');
    }
  };

  const handleBulkVcardExport = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/contacts/export/vcard`, {}, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/vcard' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contacts.vcf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(ja ? `全連絡先のvCardをダウンロードしました` : 'All contacts vCard downloaded');
    } catch (e) {
      toast.error(ja ? 'エクスポートに失敗しました' : 'Export failed');
    }
  };

  const getGoogleAccessToken = () => {
    return new Promise((resolve, reject) => {
      if (!GOOGLE_CLIENT_ID || !window.google?.accounts?.oauth2) {
        reject(new Error('Google Client ID not configured'));
        return;
      }
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/contacts',
        callback: (response) => {
          if (response.error) {
            reject(new Error(response.error));
          } else {
            resolve(response.access_token);
          }
        },
      });
      tokenClient.requestAccessToken();
    });
  };

  const handleGoogleExportSingle = async (contactId, contactName) => {
    setGoogleExporting(true);
    try {
      const accessToken = await getGoogleAccessToken();
      await axios.post(`${API_URL}/api/contacts/${contactId}/google-export`, 
        { access_token: accessToken },
        { withCredentials: true }
      );
      toast.success(ja ? `${contactName} をGoogle連絡先に追加しました` : `${contactName} added to Google Contacts`);
    } catch (e) {
      if (e.message === 'Google Client ID not configured') {
        toast.error(ja ? 'Google Client IDが設定されていません' : 'Google Client ID not configured');
      } else {
        toast.error(ja ? 'Google連絡先への追加に失敗しました' : 'Failed to add to Google Contacts');
      }
    } finally {
      setGoogleExporting(false);
    }
  };

  const handleGoogleExportBulk = async () => {
    setGoogleExporting(true);
    try {
      const accessToken = await getGoogleAccessToken();
      const result = await axios.post(`${API_URL}/api/contacts/google-export-bulk`,
        { access_token: accessToken },
        { withCredentials: true }
      );
      const { success, failed } = result.data;
      toast.success(ja ? `${success}件をGoogle連絡先に追加しました${failed > 0 ? ` (${failed}件失敗)` : ''}` : `${success} contacts added to Google${failed > 0 ? ` (${failed} failed)` : ''}`);
    } catch (e) {
      if (e.message === 'Google Client ID not configured') {
        toast.error(ja ? 'Google Client IDが設定されていません' : 'Google Client ID not configured');
      } else {
        toast.error(ja ? 'Google連絡先への一括追加に失敗しました' : 'Bulk Google export failed');
      }
    } finally {
      setGoogleExporting(false);
    }
  };

  const googleAvailable = !!GOOGLE_CLIENT_ID;

  const contactListJSX = (
    <Card className="card-cyber flex-1 flex flex-col">
      <CardHeader className="pb-3 px-3 sm:px-6">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            {t.contacts.title}
          </CardTitle>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" onClick={handleBulkVcardExport} title={ja ? 'vCard一括ダウンロード' : 'Export all as vCard'} data-testid="bulk-vcard-export-btn" className="h-8 px-2">
              <FileDown className="w-4 h-4" />
              <span className="hidden sm:inline ml-1 text-xs">vCard</span>
            </Button>
            {googleAvailable && (
              <Button size="sm" variant="outline" onClick={handleGoogleExportBulk} disabled={googleExporting} title={ja ? 'Google連絡先に一括追加' : 'Export all to Google Contacts'} data-testid="bulk-google-export-btn" className="h-8 px-2">
                {googleExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                <span className="hidden sm:inline ml-1 text-xs">Google</span>
              </Button>
            )}
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="btn-primary h-8" data-testid="add-contact-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">{t.contacts.addNew}</span>
                  <span className="sm:hidden">{user?.language === 'ja' ? '追加' : 'Add'}</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="card-cyber border-glow max-w-[95vw] sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t.contacts.addNew}</DialogTitle>
                </DialogHeader>
                <ContactForm onSubmit={handleCreate} submitText={t.common.save} />
              </DialogContent>
            </Dialog>
          </div>
        </div>
        
        <div className="flex gap-2 mt-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.search.placeholder}
              className="pl-9 input-cyber"
              data-testid="contact-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-20 sm:w-28 input-cyber" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{user?.language === 'ja' ? '全て' : 'All'}</SelectItem>
              <SelectItem value="active">{t.contacts.active}</SelectItem>
              <SelectItem value="inactive">{t.contacts.inactive}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 p-0">
        <ScrollArea className="h-full">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">{t.common.loading}</div>
          ) : contacts.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{t.contacts.noContacts}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {contacts.map((contact) => (
                <div
                  key={contact.id}
                  onClick={() => handleSelectContactMobile(contact)}
                  className={`p-3 sm:p-4 cursor-pointer hover:bg-secondary transition-colors ${
                    selectedContact?.id === contact.id ? 'bg-secondary border-l-2 border-l-primary' : ''
                  }`}
                  data-testid={`contact-item-${contact.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{contact.name}</p>
                      {contact.name_kana && (
                        <p className="text-[10px] text-muted-foreground truncate">{contact.name_kana}</p>
                      )}
                      {contact.title && (
                        <p className="text-xs text-primary/70 truncate">{contact.title}</p>
                      )}
                      {contact.company && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Building className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{contact.company}</span>
                        </p>
                      )}
                    </div>
                    <span className={`flex-shrink-0 ml-2 ${contact.status === 'active' ? 'badge-active' : 'badge-inactive'}`}>
                      {contact.status === 'active' ? t.contacts.active : t.contacts.inactive}
                    </span>
                  </div>
                  {contact.tags?.length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {contact.tags.slice(0, 3).map((tag, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs bg-secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );

  const contactDetailJSX = (
    <>
      {selectedContact && contactDashboard ? (
        <Card className="card-cyber h-full flex flex-col" data-testid="contact-detail">
          <CardHeader className="pb-3 px-3 sm:px-6">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                {/* Mobile back button */}
                <button 
                  onClick={handleBackToList} 
                  className="md:hidden text-sm text-primary mb-2 flex items-center gap-1"
                  data-testid="contact-back-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {user?.language === 'ja' ? '一覧に戻る' : 'Back to list'}
                </button>
                <CardTitle className="text-lg sm:text-xl truncate">{contactDashboard.contact.name}</CardTitle>
                {contactDashboard.contact.name_kana && (
                  <p className="text-xs text-muted-foreground">{contactDashboard.contact.name_kana}</p>
                )}
                {contactDashboard.contact.name_romaji && !contactDashboard.contact.name_kana && (
                  <p className="text-xs text-muted-foreground font-mono">{contactDashboard.contact.name_romaji}</p>
                )}
                {contactDashboard.contact.title && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <Briefcase className="w-3 h-3 text-primary" />
                    {contactDashboard.contact.title}
                  </p>
                )}
                {contactDashboard.contact.company && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Building className="w-3 h-3 text-primary" />
                    {contactDashboard.contact.company}
                  </p>
                )}
                {contactDashboard.contact.catchphrase && (
                  <p className="text-xs italic text-primary/70 mt-1">"{contactDashboard.contact.catchphrase}"</p>
                )}
              </div>
              <div className="flex gap-1.5 flex-shrink-0 flex-wrap justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownloadVcard(selectedContact.id, contactDashboard.contact.name)}
                  title={ja ? 'vCardダウンロード' : 'Download vCard'}
                  data-testid="vcard-export-btn"
                >
                  <FileDown className="w-4 h-4" />
                </Button>
                {googleAvailable && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleGoogleExportSingle(selectedContact.id, contactDashboard.contact.name)}
                    disabled={googleExporting}
                    title={ja ? 'Google連絡先に追加' : 'Add to Google Contacts'}
                    data-testid="google-export-btn"
                  >
                    {googleExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                  </Button>
                )}
                <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
                  <DialogTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(contactDashboard.contact)}
                      data-testid="edit-contact-btn"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="card-cyber border-glow max-w-[95vw] sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{t.common.edit}</DialogTitle>
                    </DialogHeader>
                    <ContactForm onSubmit={handleUpdate} submitText={t.common.save} />
                  </DialogContent>
                </Dialog>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive hover:bg-destructive/10"
                  onClick={() => handleDelete(selectedContact.id)}
                  data-testid="delete-contact-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Contact Info - Clickable actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 mt-4 text-sm">
              {contactDashboard.contact.phone && (
                <a href={`tel:${contactDashboard.contact.phone}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary hover:bg-primary/5 rounded-sm px-2 py-1.5 transition-colors cursor-pointer" data-testid="action-phone">
                  <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="truncate">{contactDashboard.contact.phone}</span>
                </a>
              )}
              {contactDashboard.contact.mobile && (
                <a href={`tel:${contactDashboard.contact.mobile}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary hover:bg-primary/5 rounded-sm px-2 py-1.5 transition-colors cursor-pointer" data-testid="action-mobile">
                  <Smartphone className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="truncate">{contactDashboard.contact.mobile}</span>
                </a>
              )}
              {contactDashboard.contact.fax && (
                <a href={`tel:${contactDashboard.contact.fax}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary hover:bg-primary/5 rounded-sm px-2 py-1.5 transition-colors cursor-pointer" data-testid="action-fax">
                  <Printer className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="truncate">FAX: {contactDashboard.contact.fax}</span>
                </a>
              )}
              {contactDashboard.contact.email && (
                <a href={`mailto:${contactDashboard.contact.email}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary hover:bg-primary/5 rounded-sm px-2 py-1.5 transition-colors cursor-pointer truncate" data-testid="action-email">
                  <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="truncate">{contactDashboard.contact.email}</span>
                </a>
              )}
              {contactDashboard.contact.address && (
                <a href={`https://www.google.com/maps/search/${encodeURIComponent(contactDashboard.contact.address)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-muted-foreground hover:text-primary hover:bg-primary/5 rounded-sm px-2 py-1.5 transition-colors cursor-pointer sm:col-span-2" data-testid="action-address">
                  <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="truncate">{contactDashboard.contact.address}</span>
                </a>
              )}
            </div>
          </CardHeader>
          
          <CardContent className="flex-1 p-0 overflow-hidden">
            <Tabs defaultValue="files" className="h-full flex flex-col">
              <TabsList className="px-2 sm:px-4 bg-transparent border-b border-border rounded-none h-auto justify-start overflow-x-auto flex-nowrap">
                <TabsTrigger value="files" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none text-xs sm:text-sm whitespace-nowrap">
                  <FileText className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">{user?.language === 'ja' ? '書類' : 'Files'}</span> ({contactDashboard.files?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="media" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none text-xs sm:text-sm whitespace-nowrap">
                  <Image className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">{user?.language === 'ja' ? 'メディア' : 'Media'}</span> ({contactDashboard.media?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="tasks" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none text-xs sm:text-sm whitespace-nowrap">
                  <CheckSquare className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">{user?.language === 'ja' ? 'タスク' : 'Tasks'}</span> ({contactDashboard.tasks?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="timeline" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none text-xs sm:text-sm whitespace-nowrap">
                  <History className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">{user?.language === 'ja' ? '履歴' : 'Timeline'}</span>
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="files" className="flex-1 p-3 sm:p-4 m-0">
                <ScrollArea className="h-[300px]">
                  {contactDashboard.files?.length > 0 ? (
                    <div className="space-y-2">
                      {contactDashboard.files.map((file) => (
                        <div key={file.id} className="flex items-center gap-3 p-3 rounded-sm bg-secondary/50 hover:bg-secondary">
                          <FileText className="w-5 h-5 text-primary flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate text-sm">{file.title}</p>
                            <p className="text-xs text-muted-foreground font-mono">{formatDate(file.created_at)}</p>
                          </div>
                          {file.is_business_card && (
                            <Badge className="bg-primary/20 text-primary text-xs hidden sm:flex">{t.files.businessCard}</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{t.files.noFiles}</p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="media" className="flex-1 p-3 sm:p-4 m-0">
                <ScrollArea className="h-[300px]">
                  {contactDashboard.media?.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {contactDashboard.media.map((item) => (
                        <div 
                          key={item.id} 
                          className="aspect-square bg-secondary rounded-sm overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                          onClick={() => setMediaPreview(item)}
                          data-testid={`media-item-${item.id}`}
                        >
                          {item.type === 'image' ? (
                            <img
                              src={`${API_URL}/api/media/${item.id}/stream`}
                              alt={item.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Image className="w-8 h-8 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Image className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{t.media.noMedia}</p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="tasks" className="flex-1 p-3 sm:p-4 m-0">
                <ScrollArea className="h-[300px]">
                  {contactDashboard.tasks?.length > 0 ? (
                    <div className="space-y-2">
                      {contactDashboard.tasks.map((task) => (
                        <div key={task.id} className="flex items-center gap-3 p-3 rounded-sm bg-secondary/50">
                          <CheckSquare className={`w-5 h-5 flex-shrink-0 ${task.done ? 'text-green-400' : 'text-yellow-400'}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium text-sm ${task.done ? 'line-through text-muted-foreground' : ''}`}>{task.title}</p>
                            {task.due_date && (
                              <p className="text-xs text-muted-foreground font-mono">{formatDate(task.due_date)}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{t.tasks.noTasks}</p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="timeline" className="flex-1 p-3 sm:p-4 m-0">
                <ScrollArea className="h-[300px]">
                  {contactDashboard.activities?.length > 0 ? (
                    <div className="space-y-3 border-l-2 border-border pl-4 ml-2">
                      {contactDashboard.activities.map((activity, idx) => (
                        <div key={activity.id || idx} className="relative">
                          <div className="absolute -left-[21px] w-2 h-2 rounded-full bg-primary" />
                          <p className="text-sm">{activity.description}</p>
                          <p className="text-xs text-muted-foreground font-mono">{formatDate(activity.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{user?.language === 'ja' ? '履歴がありません' : 'No activity history'}</p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      ) : (
        <Card className="card-cyber h-full flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <Users className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 opacity-30" />
            <p className="text-sm">{user?.language === 'ja' ? '連絡先を選択してください' : 'Select a contact to view details'}</p>
          </div>
        </Card>
      )}
    </>
  );

  return (
    <div className="h-[calc(100vh-100px)]" data-testid="contacts-page">
      {/* Desktop: side-by-side layout */}
      <div className="hidden md:flex h-full gap-4">
        <div className="w-1/3 flex flex-col">
          {contactListJSX}
        </div>
        <div className="flex-1">
          {contactDetailJSX}
        </div>
      </div>

      {/* Mobile: toggle between list and detail */}
      <div className="md:hidden h-full flex flex-col">
        {mobileShowDetail && selectedContact ? (
          contactDetailJSX
        ) : (
          contactListJSX
        )}
      </div>

      {/* Media Preview Dialog */}
      <Dialog open={!!mediaPreview} onOpenChange={() => setMediaPreview(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] card-cyber border-glow p-0 overflow-hidden">
          <DialogHeader className="p-3 sm:p-4 border-b border-border flex-shrink-0">
            <div className="flex items-center justify-between gap-2">
              <DialogTitle className="text-sm sm:text-base truncate">{mediaPreview?.title}</DialogTitle>
              <div className="flex items-center gap-1 flex-shrink-0">
                {mediaPreview && (
                  <a
                    href={`${API_URL}/api/media/${mediaPreview.id}/stream`}
                    download={mediaPreview.original_filename || mediaPreview.title}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-sm hover:bg-secondary transition-colors"
                    data-testid="media-download-btn"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                )}
                <button onClick={() => setMediaPreview(null)} className="inline-flex items-center justify-center h-8 w-8 rounded-sm hover:bg-secondary transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            {mediaPreview?.tags?.length > 0 && (
              <div className="flex gap-1 mt-1">
                {mediaPreview.tags.map((tag, i) => (
                  <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                ))}
              </div>
            )}
          </DialogHeader>
          <div className="flex items-center justify-center bg-black/30 p-2 sm:p-4" style={{ maxHeight: 'calc(90vh - 80px)' }}>
            {mediaPreview && (
              <img
                src={`${API_URL}/api/media/${mediaPreview.id}/stream`}
                alt={mediaPreview.title}
                className="max-w-full max-h-[70vh] object-contain rounded-sm"
                data-testid="media-preview-image"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactsPage;
